import { ExportTargetDto } from './export';

export interface QueryHierarchy {
  category: string;
  childCategories: QueryHierarchy[];
  queries: QueryDto[];
}

export const findQuery = (
  root: QueryHierarchy,
  id: number
): QueryDto | null => {
  for (let i = 0; i < root.queries.length; i++) {
    if (root.queries[i].id === id) {
      return root.queries[i];
    }
  }
  for (let i = 0; i < root.childCategories.length; i++) {
    const elem = findQuery(root.childCategories[i], id);
    if (elem !== null) {
      return elem;
    }
  }

  return null;
};

export const commentColumnSql = (value: string) => `/*${value}*/`;
export const uncommentColumnSql = (value: string) =>
  isColumnCommented(value) ? value.substring(2, value.length - 2) : value;
export const isColumnCommented = (value: string) =>
  value.startsWith('/*') && value.endsWith('*/');

export interface QueryDto {
  id: number;
  name: string;
}

export interface Query {
  id: number;
  name: string;
  parameters: Parameter[];
  storesAvailableIn: string[];
}

export interface BusinessUnitDto {
  id: string; // in the format of STO-XXX or CDC_XXX
  buCode: string;
  buName: string;
  languages: string[];
  bcp47Languages: string[];
}

export interface JobFormDto {
  id: number | undefined;
  title: string;
  description: string;
  repetition: Repetition;
  storesIds: string[];
  queryId: number;
  isActive: boolean;
  queryParameters: Parameter[];
  activeFrom: string;
  activeTo: string;
  exportTargetSettings: ExportTargetDto | null;
}

export interface JobFormData {
  id: number | undefined;
  title: string;
  description: string;
  repetition: Repetition;
  storesIds: string[];
  query: Query;
  isActive: boolean;
  queryParameters: Parameter[];
  activeFrom: string;
  activeTo: string;
  exportTargetSettings: ExportTargetDto | null;
}

export interface JobDetailsDto {
  id: number | undefined;
  title: string;
  description: string;
  authorName: string;
  isActive: boolean;
  exportStatus: JobStatusInfo;
  repetition: Repetition;
  stores: BusinessUnitDto[];
  query: QueryDto;
  queryParameters: Parameter[];
  activeFrom: string;
  activeTo: string;
  exportTargetSettings: ExportTargetDto | null;
}

export interface JobStatusInfo {
  status: JobStatus;
  date: string;
}

export enum JobStatus {
  Scheduled = 'Scheduled',
  Running = 'Running',
  Failed = 'Failing',
  Inactive = 'Inactive',
  WithWarnings = 'Warnings',
}

export type RepetitionType =
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'personalized'
  | 'afterNightBatch'
  | 'afterMorningBatch';

export enum ParameterType {
  Text = 'text',
  Code = 'code',
  Numeric = 'numeric',
  Language = 'language',
  LanguageBCP47 = 'languageBCP47',
  Date = 'date',
  DateWeek = 'dateWeek',
  Set = 'set',
  ColumnOutput = 'columnOutput',
  TemplateVariable = 'templateVariable',
}

export interface Repetition {
  type: RepetitionType;
  cronValue?: string;
}

export interface Parameter {
  visibleName: string;
  parameterType: ParameterType;
  defaultValue: string;
  operator: string;
  id: number;
  value: string;
  codes: ParamCode[];
  queryId?: number;
  parameterId?: number;
}

export interface ParamCode {
  value: string;
  description: string;
}

export interface JobOverview {
  id: number;
  title: string;
  description: string;
  repetition: Repetition;
  activeFrom: string;
  activeTo: string;
  stores: BusinessUnitDto[];
  queryName: string;
  authorName: string;
  status: string;
  date: string | undefined;
}

export interface JobActiveStatus {
  isActive: boolean;
}
